import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./Filters.css";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { MdOutlineDeleteOutline, MdEdit, MdSave, MdClose, MdExpandMore } from "react-icons/md";
import { FaPlus } from "react-icons/fa";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ClipLoader from "react-spinners/ClipLoader";

/* ═══════════════════════════════════════════════════════════════════════════
   FILTERS

   The admin mirror of the storefront's filter panel, in the storefront's own
   order so the two read the same:

     COLORS · SIZE · PRICE   automatic, read-only. Derived from the products
                             themselves — the same derivation the storefront
                             does — so they cannot go stale and there is
                             nothing here to manage.

     TYPE                    the one the owner builds. Add a type, name it,
                             then pick the products that belong in it.

   NOTHING ON THIS PAGE WRITES TO A PRODUCT. Membership is a list of product
   ids stored on the garment TYPE. Picking a product adds its id to that list;
   removing it takes the id out. The product document is never opened and
   never modified -- not on add, not on rename, not on delete.

   That is deliberate, and it is the owner's own rule: "this section is
   totally different from the products I update, nothing I do here should
   affect the real name or real data of the website." Keeping the ids on the
   type means the worst a mistake here can do is put an item in the wrong
   group, and undoing it is one click.

   The `productType` shown against a product in the picker is therefore a
   LOOKUP, resolved server-side from the type lists -- not a field read off
   the product.

   Colours and sizes come from each product's `stock` array. `attributes.color`
   is free text ("red, white") and is not usable as a facet.
   ═══════════════════════════════════════════════════════════════════════════ */

const API = "https://admin.riymay.com/api/v1/admin";

const money = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  try {
    return "₹" + num.toLocaleString("en-IN");
  } catch (e) {
    return "₹" + Math.round(num);
  }
};

const same = (a, b) =>
  String(a == null ? "" : a).trim().toLowerCase() ===
  String(b == null ? "" : b).trim().toLowerCase();

const Filters = () => {
  const { garmentTypes, fetchGarmentTypes } = useAdminAuth();

  const [overview, setOverview] = useState(null);
  const [loadingOverview, setLoadingOverview] = useState(true);

  const [newTypeName, setNewTypeName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [pickerValue, setPickerValue] = useState({});
  const [busy, setBusy] = useState({ add: false, edit: false, delete: null, toggle: null, assign: null });

  const adminToken = localStorage.getItem("admintoken");
  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${adminToken}` }),
    [adminToken]
  );

  const loadOverview = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/get/filter-overview`, { headers: authHeader });
      setOverview(data && data.response ? data.response : null);
    } catch (error) {
      console.error("Error loading filter overview:", error);
      toast.error("Could not read the filter options from your products");
    } finally {
      setLoadingOverview(false);
    }
  }, [authHeader]);

  useEffect(() => {
    fetchGarmentTypes();
    loadOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const products = (overview && overview.products) || [];

  // Which products sit in each type, and which are still untyped.
  const productsInType = useCallback(
    (typeName) => products.filter((p) => same(p.productType, typeName)),
    [products]
  );

  const untypedCount = products.filter((p) => !String(p.productType || "").trim()).length;

  /* ── TYPE actions ─────────────────────────────────────────────────────── */

  const handleAddType = async (e) => {
    e.preventDefault();
    if (!newTypeName.trim()) {
      toast.error("Give the type a name first");
      return;
    }
    try {
      setBusy((s) => ({ ...s, add: true }));
      await axios.post(`${API}/create/garment-type`, { name: newTypeName.trim() }, { headers: authHeader });
      toast.success(`'${newTypeName.trim()}' added`);
      setNewTypeName("");
      fetchGarmentTypes();
    } catch (error) {
      console.error("Error creating type:", error);
      toast.error(error.response?.data?.message || "Failed to add the type");
    } finally {
      setBusy((s) => ({ ...s, add: false }));
    }
  };

  // Renaming is a two-part change and the server does both in one call: the
  // type row AND every product carrying the old name. Without the second part
  // the storefront would quietly lose those products.
  const handleRename = async (type) => {
    if (!editName.trim()) {
      toast.error("The name cannot be empty");
      return;
    }
    if (editName.trim() === type.name) {
      setEditId(null);
      return;
    }

    const affected = productsInType(type.name).length;
    const confirm = await Swal.fire({
      title: `Rename '${type.name}' to '${editName.trim()}'?`,
      html:
        affected > 0
          ? `<b>${affected}</b> product${affected === 1 ? "" : "s"} currently in this type will be moved to the new name automatically, so nothing disappears from the shop.`
          : "No products are in this type yet.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Rename",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#3085d6",
    });
    if (!confirm.isConfirmed) return;

    try {
      setBusy((s) => ({ ...s, edit: true }));
      const { data } = await axios.patch(
        `${API}/update/garment-type`,
        { garmentTypeId: type._id, name: editName.trim() },
        { headers: authHeader }
      );
      const renamed = data?.response?.productsRenamed ?? 0;
      toast.success(
        renamed > 0
          ? `Renamed — ${renamed} product${renamed === 1 ? "" : "s"} moved with it`
          : "Renamed"
      );
      setEditId(null);
      setEditName("");
      fetchGarmentTypes();
      loadOverview();
    } catch (error) {
      console.error("Error renaming type:", error);
      // 409: a type with that name already exists. We refuse rather than merge
      // the two — merging is not undoable and is never what a typo meant.
      toast.error(
        error.response?.status === 409
          ? `A type called '${editName.trim()}' already exists. Rename it to something else, or move the products across one by one.`
          : error.response?.data?.message || "Failed to rename"
      );
    } finally {
      setBusy((s) => ({ ...s, edit: false }));
    }
  };

  const handleToggleActive = async (type) => {
    try {
      setBusy((s) => ({ ...s, toggle: type._id }));
      await axios.patch(
        `${API}/update/garment-type`,
        { garmentTypeId: type._id, active: !type.active },
        { headers: authHeader }
      );
      toast.success(
        !type.active
          ? `'${type.name}' is showing in the shop filters again`
          : `'${type.name}' is hidden from the shop filters`
      );
      fetchGarmentTypes();
    } catch (error) {
      console.error("Error toggling type:", error);
      toast.error("Failed to update the type");
    } finally {
      setBusy((s) => ({ ...s, toggle: null }));
    }
  };

  const handleDeleteType = async (type) => {
    const inType = productsInType(type.name);
    const confirm = await Swal.fire({
      title: `Delete the type '${type.name}'?`,
      html:
        inType.length > 0
          ? `<p style="margin:0 0 8px"><b>${inType.length}</b> product${inType.length === 1 ? " is" : "s are"} in this type.</p>
             <p style="margin:0 0 8px">Those products are <b>NOT deleted</b>. They stay in the shop with their price, stock and photos exactly as they are — they simply go back to having no type, and stop showing under this filter.</p>
             <p style="margin:0">The type itself disappears from the shop's filter list.</p>`
          : "No products are in this type. It just disappears from the shop's filter list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: inType.length > 0 ? `Delete type, keep the ${inType.length} product${inType.length === 1 ? "" : "s"}` : "Delete type",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;

    try {
      setBusy((s) => ({ ...s, delete: type._id }));
      const { data } = await axios.delete(`${API}/delete/garment-type?id=${type._id}`, {
        headers: authHeader,
      });
      const cleared = data?.response?.productsCleared ?? 0;
      toast.success(
        cleared > 0
          ? `Type deleted — ${cleared} product${cleared === 1 ? "" : "s"} kept, now untyped`
          : "Type deleted"
      );
      fetchGarmentTypes();
      loadOverview();
    } catch (error) {
      console.error("Error deleting type:", error);
      toast.error("Failed to delete the type");
    } finally {
      setBusy((s) => ({ ...s, delete: null }));
    }
  };

  /* ── product assignment ───────────────────────────────────────────────── */

  const handleAssign = async (type) => {
    const productId = pickerValue[type._id];
    if (!productId) {
      toast.error("Pick a product from the list first");
      return;
    }
    try {
      setBusy((s) => ({ ...s, assign: type._id }));
      const { data } = await axios.patch(
        `${API}/assign/products/garment-type`,
        { garmentTypeId: type._id, productIds: [productId] },
        { headers: authHeader }
      );
      const report = data?.response || {};
      const ok = (report.assigned || []).length;
      const bad = report.failed || [];
      if (ok) toast.success(`Added to '${type.name}'`);
      // The server reads the products back and reports what actually landed,
      // so a silent partial failure shows up here instead of looking fine.
      bad.forEach((f) => toast.error(`${f.name || f.productId}: ${f.reason}`));
      setPickerValue((s) => ({ ...s, [type._id]: "" }));
      loadOverview();
    } catch (error) {
      console.error("Error assigning product:", error);
      toast.error(error.response?.data?.message || "Failed to add the product");
    } finally {
      setBusy((s) => ({ ...s, assign: null }));
    }
  };

  const handleUnassign = async (type, product) => {
    try {
      setBusy((s) => ({ ...s, assign: type._id }));
      const { data } = await axios.patch(
        `${API}/unassign/products/garment-type`,
        { productIds: [product._id] },
        { headers: authHeader }
      );
      const report = data?.response || {};
      if ((report.assigned || []).length) {
        toast.success(`'${product.name}' removed from '${type.name}'`);
      }
      (report.failed || []).forEach((f) => toast.error(`${f.name || f.productId}: ${f.reason}`));
      loadOverview();
    } catch (error) {
      console.error("Error removing product from type:", error);
      toast.error("Failed to remove the product");
    } finally {
      setBusy((s) => ({ ...s, assign: null }));
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */

  const colors = (overview && overview.colors) || [];
  const sizes = (overview && overview.sizes) || [];
  const price = (overview && overview.price) || { hasPrices: false };

  const autoNote = "Automatic — this updates itself as you add products. Nothing to manage here.";

  return (
    <div className="filters-page-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <h1 className="page-title">Filters</h1>
      <p className="page-subtitle">
        What shoppers can filter by on the shop. Colours, sizes and price look
        after themselves — they are read from your products. Type is the one
        you build: name it, then choose which products go in it.
      </p>

      {/* ── COLORS ───────────────────────────────────────────────────── */}
      <section className="filter-section">
        <div className="filter-section-head">
          <h2 className="filter-section-title">Colors</h2>
          <span className="auto-pill">Automatic</span>
          <span className="filter-count">
            {loadingOverview ? "" : `${colors.length} in the shop`}
          </span>
        </div>
        <p className="filter-section-note">{autoNote}</p>
        {loadingOverview ? (
          <ClipLoader size={20} color="#3085d6" />
        ) : colors.length === 0 ? (
          <p className="empty-note">No colours yet — they appear as soon as products have colours on them.</p>
        ) : (
          <div className="chip-row">
            {colors.map((c) => (
              <span key={c.key} className="chip">{c.label}</span>
            ))}
          </div>
        )}
      </section>

      {/* ── SIZE ─────────────────────────────────────────────────────── */}
      <section className="filter-section">
        <div className="filter-section-head">
          <h2 className="filter-section-title">Size</h2>
          <span className="auto-pill">Automatic</span>
          <span className="filter-count">
            {loadingOverview ? "" : `${sizes.length} in the shop`}
          </span>
        </div>
        <p className="filter-section-note">{autoNote}</p>
        {loadingOverview ? (
          <ClipLoader size={20} color="#3085d6" />
        ) : sizes.length === 0 ? (
          <p className="empty-note">No sizes yet — they appear as soon as products have stock rows.</p>
        ) : (
          <div className="chip-row">
            {sizes.map((s) => (
              <span key={s.key} className="chip">{s.label}</span>
            ))}
          </div>
        )}
      </section>

      {/* ── PRICE ────────────────────────────────────────────────────── */}
      <section className="filter-section">
        <div className="filter-section-head">
          <h2 className="filter-section-title">Price</h2>
          <span className="auto-pill">Automatic</span>
        </div>
        <p className="filter-section-note">{autoNote}</p>
        {loadingOverview ? (
          <ClipLoader size={20} color="#3085d6" />
        ) : !price.hasPrices ? (
          <p className="empty-note">No prices yet.</p>
        ) : (
          <div className="price-range">
            <div className="price-range-item">
              <span className="price-range-label">Cheapest</span>
              <span className="price-range-value">{money(price.min)}</span>
            </div>
            <div className="price-range-dash" />
            <div className="price-range-item">
              <span className="price-range-label">Most expensive</span>
              <span className="price-range-value">{money(price.max)}</span>
            </div>
          </div>
        )}
      </section>

      {/* ── TYPE ─────────────────────────────────────────────────────── */}
      <section className="filter-section type-section">
        <div className="filter-section-head">
          <h2 className="filter-section-title">Type</h2>
          <span className="manual-pill">You manage this</span>
          <span className="filter-count">
            {garmentTypes.length} type{garmentTypes.length === 1 ? "" : "s"}
            {overview ? ` · ${untypedCount} product${untypedCount === 1 ? "" : "s"} with no type` : ""}
          </span>
        </div>
        <p className="filter-section-note">
          Name a type — Hoodie, Shirt, Shacket — then open it and choose the
          products that belong in it.
        </p>

        <form className="add-type-form" onSubmit={handleAddType}>
          <input
            type="text"
            className="add-type-input"
            placeholder="Name a new type, e.g. Hoodie"
            value={newTypeName}
            onChange={(e) => setNewTypeName(e.target.value)}
          />
          <button type="submit" className="add-type-button" disabled={busy.add}>
            {busy.add ? <ClipLoader size={16} color="#ffffff" /> : (<><FaPlus /> Add</>)}
          </button>
        </form>

        {garmentTypes.length === 0 ? (
          <p className="empty-note">
            No types yet. Add your first one above — until then the shop shows no Type filter at all.
          </p>
        ) : (
          <div className="type-list">
            {garmentTypes.map((type) => {
              const inType = productsInType(type.name);
              const isOpen = expandedId === type._id;
              const available = products.filter((p) => !same(p.productType, type.name));

              return (
                <div key={type._id} className={`type-row ${isOpen ? "open" : ""}`}>
                  <div className="type-row-head">
                    {editId === type._id ? (
                      <div className="type-edit">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="type-edit-input"
                          placeholder="New name"
                        />
                        <button
                          type="button"
                          className="icon-btn save"
                          onClick={() => handleRename(type)}
                          disabled={busy.edit}
                          title="Save the new name"
                        >
                          {busy.edit ? <ClipLoader size={14} color="#ffffff" /> : <MdSave />}
                        </button>
                        <button
                          type="button"
                          className="icon-btn cancel"
                          onClick={() => setEditId(null)}
                          title="Cancel"
                        >
                          <MdClose />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="type-name-button"
                        onClick={() => setExpandedId(isOpen ? null : type._id)}
                        aria-expanded={isOpen}
                      >
                        <MdExpandMore className={`type-caret ${isOpen ? "up" : ""}`} />
                        <span className="type-name">{type.name}</span>
                        <span className="type-count">
                          {inType.length} product{inType.length === 1 ? "" : "s"}
                        </span>
                        {!type.active && <span className="hidden-pill">hidden from shop</span>}
                      </button>
                    )}

                    <div className="type-row-actions">
                      <label className="active-toggle" title="Show this type in the shop's filters">
                        <input
                          type="checkbox"
                          checked={!!type.active}
                          onChange={() => handleToggleActive(type)}
                          disabled={busy.toggle === type._id}
                        />
                        <span>Show in shop</span>
                      </label>
                      <button
                        type="button"
                        className="text-btn"
                        onClick={() => {
                          setEditId(type._id);
                          setEditName(type.name);
                        }}
                      >
                        <MdEdit /> Rename
                      </button>
                      <button
                        type="button"
                        className="text-btn danger"
                        onClick={() => handleDeleteType(type)}
                        disabled={busy.delete === type._id}
                      >
                        {busy.delete === type._id ? (
                          <ClipLoader size={14} color="#d33" />
                        ) : (
                          <><MdOutlineDeleteOutline /> Delete</>
                        )}
                      </button>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="type-row-body">
                      <div className="assign-row">
                        <label htmlFor={`picker-${type._id}`} className="assign-label">
                          Add products
                        </label>
                        <select
                          id={`picker-${type._id}`}
                          className="assign-select"
                          value={pickerValue[type._id] || ""}
                          onChange={(e) =>
                            setPickerValue((s) => ({ ...s, [type._id]: e.target.value }))
                          }
                        >
                          <option value="">Choose a product…</option>
                          {available.map((p) => (
                            <option key={p._id} value={p._id}>
                              {p.name}
                              {p.productType ? ` — currently in ${p.productType}` : ""}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          className="assign-button"
                          onClick={() => handleAssign(type)}
                          disabled={busy.assign === type._id || !pickerValue[type._id]}
                        >
                          {busy.assign === type._id ? (
                            <ClipLoader size={14} color="#ffffff" />
                          ) : (
                            "Add to type"
                          )}
                        </button>
                      </div>
                      <p className="assign-note">
                        Picking a product here moves it into <b>{type.name}</b>. If it was
                        in another type, it leaves that one.
                      </p>

                      {inType.length === 0 ? (
                        <p className="empty-note">Nothing in this type yet.</p>
                      ) : (
                        <ul className="assigned-list">
                          {inType.map((p) => (
                            <li key={p._id} className="assigned-item">
                              <span className="assigned-name">{p.name}</span>
                              <span className="assigned-price">{money(p.price)}</span>
                              <button
                                type="button"
                                className="text-btn danger small"
                                onClick={() => handleUnassign(type, p)}
                                disabled={busy.assign === type._id}
                                title="Take this product out of the type"
                              >
                                <MdClose /> Remove
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Filters;
